import { useCallback, useEffect, useRef } from 'react';

import { DEBUG } from '../lib/debug';
import { writeDebugBundle } from '../lib/debug-folder';
import { rotateBlob90Clockwise } from '../lib/image-rotate';
import type { ManualQuad, WorkerRequest, WorkerResponse } from '../lib/types';
import { uuid } from '../lib/uuid';
import { useAppStore } from '../store';

interface QueueItem {
  id: string;
  file: File;
  lang: string;
  manualQuad?: ManualQuad;
}

export interface ReceiptProcessor {
  /** Enqueue one or more files for processing across the worker pool. */
  processFiles: (files: File[], lang: string) => void;
  /** Cancel everything in flight + queued and tear down the pool. */
  cancel: () => void;
  /**
   * DEBUG-only: set the directory the hook auto-saves debug bundles to.
   * Pass null to disable. No-op when DEBUG is false.
   */
  setDebugFolder: (handle: FileSystemDirectoryHandle | null) => void;
  /**
   * Reprocess an existing session with caller-supplied corner positions
   * instead of running auto scan-correct. Used by the manual-crop modal
   * after the user adjusts the four-corner overlay. Looks up the
   * session's stored originalFile and re-dispatches it through the worker
   * pool, reusing the same session id so the UI updates in place.
   */
  reprocessWithManualQuad: (id: string, manualQuad: ManualQuad, lang: string) => void;
  /**
   * Rotate a session's corrected image 90° clockwise client-side and
   * re-dispatch it through the standard pipeline so OCR runs on the
   * right orientation. The rotated File becomes the session's new
   * originalFile so further rotates / recrops work from the rotated
   * image (each click is +90° from the current orientation). Returns
   * a promise that resolves once dispatch starts (not when processing
   * completes); the UI watches the session state for that.
   */
  reprocessWithRotation: (id: string, lang: string) => Promise<void>;
}

/**
 * How many OCR workers to run in parallel. Adapts to the client's
 * hardware so we get aggressive throughput on desktop without OOMing
 * mobile.
 *
 * Two regimes:
 *   - **Resource-constrained** (mobile devices, or Chromium browsers
 *     reporting ≤2 GB device memory): cap the pool at 2. A mobile
 *     Safari tab dies past ~1 GB total, and that budget has to cover
 *     the whole React tree + image buffers + scan-correct WebGL
 *     textures + Tesseract instances — only some of which are this
 *     pool. Two workers is enough to overlap one OCR pass with
 *     another's preprocessing without crowding the budget.
 *   - **Desktop / high-RAM**: spawn one worker per logical core
 *     minus 2, leaving headroom for the main thread, the OS, and the
 *     user's other tabs. So a 16-thread desktop runs 14 workers, an
 *     8-thread laptop 6, a 4-thread machine 2.
 *
 * Tesseract.js v5 also bursts up to ~4 SAB threads INTERNALLY per
 * worker (when nginx COOP/COEP lights up cross-origin isolation), so
 * the actual thread count is ~4N — the OS scheduler time-shares them
 * across the cores we left available.
 *
 * Each Tesseract instance holds ~80 MB. A 16-thread desktop pool of
 * 14 ≈ 1.1 GB, fine on 8+ GB RAM. Mobile worst case is 2 workers ≈
 * 160 MB Tesseract — well under the tab budget.
 *
 * Falls back to 1 worker when navigator.hardwareConcurrency is missing
 * (older Safari, some embedded browsers).
 */
function computePoolSize(): number {
  const hw = navigator.hardwareConcurrency ?? 2;
  if (isResourceConstrained()) {
    return Math.min(2, Math.max(1, Math.floor(hw / 2)));
  }
  return Math.max(1, hw - 2);
}

function isResourceConstrained(): boolean {
  return isMobileDevice() || isLowMemoryDevice();
}

/**
 * Strongest signal: Chromium's UA-CH `userAgentData.mobile`. Falls back
 * to a UA-string regex for Safari / Firefox where UA-CH isn't shipped.
 * False negatives on edge browsers are acceptable — they fall through
 * to the desktop branch and we'd just over-spawn workers a bit.
 */
function isMobileDevice(): boolean {
  const uaData = (
    navigator as { userAgentData?: { mobile?: boolean } }
  ).userAgentData;
  if (uaData?.mobile === true) return true;
  if (uaData?.mobile === false) return false;
  if (typeof navigator.userAgent === 'string') {
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  }
  return false;
}

/**
 * `navigator.deviceMemory` is Chromium-only and reports RAM in GB,
 * rounded down + capped at 8 for fingerprinting resistance. ≤2 GB is
 * a real signal that we should stay small; absent or ≥4 means we
 * trust the desktop / hardwareConcurrency path.
 */
function isLowMemoryDevice(): boolean {
  const dm = (navigator as { deviceMemory?: number }).deviceMemory;
  return typeof dm === 'number' && dm <= 2;
}

/**
 * Owns a pool of OCR Web Workers and a shared per-file queue.
 *
 * Each enqueued file is dispatched to whichever worker is idle. Each
 * worker keeps its own persistent Tesseract instance (initialised
 * lazily on first request, reused for the session — see
 * `pipeline/ocr.ts`). For batch drops on multi-core machines this is
 * roughly an N× speed-up vs the single-worker design that came before.
 *
 * StrictMode safety: the cleanup in the unmount effect terminates every
 * worker. The queue + in-flight maps live in refs so they survive the
 * StrictMode double-invoke without spawning duplicate workers.
 */
export function useReceiptProcessor(): ReceiptProcessor {
  const workersRef = useRef<Worker[]>([]);
  /** Indices into workersRef of currently-idle workers. Acts as a stack. */
  const idleWorkerIdxRef = useRef<number[]>([]);
  /** workerIndex → sessionId currently being processed by that worker. */
  const inFlightByWorkerRef = useRef<Map<number, string>>(new Map());
  /** sessionId → workerIndex (reverse lookup for crash / cancel routing). */
  const workerByIdRef = useRef<Map<string, number>>(new Map());
  const queueRef = useRef<QueueItem[]>([]);
  // DEBUG-only: original File handles, kept until the corresponding result
  // arrives so we can include the original in the auto-saved debug bundle.
  // Cleared after each save (or on error) to avoid retaining file blobs.
  const inFlightFilesRef = useRef<Map<string, File>>(new Map());
  const debugFolderRef = useRef<FileSystemDirectoryHandle | null>(null);

  const pump = useCallback((): void => {
    // Drain as many queued items as there are idle workers.
    while (queueRef.current.length > 0 && idleWorkerIdxRef.current.length > 0) {
      const next = queueRef.current.shift()!;
      const workerIdx = idleWorkerIdxRef.current.shift()!;
      const worker = workersRef.current[workerIdx];
      if (worker === undefined) {
        // Worker was terminated mid-flight (cancel?). Drop the slot,
        // re-queue the item, stop pumping.
        queueRef.current.unshift(next);
        return;
      }
      inFlightByWorkerRef.current.set(workerIdx, next.id);
      workerByIdRef.current.set(next.id, workerIdx);
      if (DEBUG) inFlightFilesRef.current.set(next.id, next.file);
      const request: WorkerRequest = {
        kind: 'process',
        id: next.id,
        file: next.file,
        lang: next.lang,
      };
      if (next.manualQuad !== undefined) request.manualQuad = next.manualQuad;
      worker.postMessage(request);
    }
  }, []);

  const finishOnWorker = useCallback(
    (id: string, idx: number): void => {
      inFlightByWorkerRef.current.delete(idx);
      workerByIdRef.current.delete(id);
      idleWorkerIdxRef.current.push(idx);
      pump();
    },
    [pump],
  );

  const attachListeners = useCallback(
    (worker: Worker, idx: number): void => {
      worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
        const msg = event.data;
        const store = useAppStore.getState();
        switch (msg.kind) {
          case 'stage':
            store.setStage(msg.id, msg.stage, msg.progress, msg.message);
            break;
          case 'result':
            store.setResult(msg.id, msg.receipt, msg.ocr, msg.findings);
            if (DEBUG && debugFolderRef.current !== null) {
              const folder = debugFolderRef.current;
              const file = inFlightFilesRef.current.get(msg.id);
              if (file !== undefined) {
                // Read the just-updated session for the corrected image and
                // debug log (both written by store mutations earlier in the
                // same message-loop turn).
                const session = useAppStore
                  .getState()
                  .sessions.find((s) => s.id === msg.id);
                if (session !== undefined) {
                  void writeDebugBundle(folder, {
                    filename: session.filename,
                    originalFile: file,
                    correctedImage: session.correctedImage,
                    debugLog: session.debugLog,
                    ocr: session.ocrDebug,
                    receipt: session.receipt,
                  });
                }
              }
            }
            if (DEBUG) inFlightFilesRef.current.delete(msg.id);
            finishOnWorker(msg.id, idx);
            break;
          case 'error':
            store.setError(msg.id, msg.error, msg.stage);
            if (DEBUG) inFlightFilesRef.current.delete(msg.id);
            finishOnWorker(msg.id, idx);
            break;
          case 'debug':
            store.appendDebug(msg.id, msg.entry);
            break;
          case 'corrected-image':
            store.setCorrectedImage(msg.id, {
              blob: msg.blob,
              width: msg.width,
              height: msg.height,
              changed: msg.changed,
            });
            break;
        }
      });
      worker.addEventListener('error', (event: ErrorEvent) => {
        // Worker crashed mid-execution. Surface to whichever session was
        // assigned to this worker, then free the slot.
        const id = inFlightByWorkerRef.current.get(idx);
        if (id !== undefined) {
          useAppStore.getState().setError(id, event.message || 'Worker crashed');
          if (DEBUG) inFlightFilesRef.current.delete(id);
          finishOnWorker(id, idx);
        }
      });
      worker.addEventListener('messageerror', (event) => {
        console.error('worker messageerror (deserialization failed):', event);
      });
    },
    [finishOnWorker],
  );

  const ensureWorkerPool = useCallback((): void => {
    const target = computePoolSize();
    while (workersRef.current.length < target) {
      // Vite recognizes this exact pattern and bundles the worker as a
      // separate chunk. Don't refactor without checking Vite's worker docs.
      const worker = new Worker(
        new URL('../workers/ocr.worker.ts', import.meta.url),
        { type: 'module' },
      );
      const idx = workersRef.current.length;
      workersRef.current.push(worker);
      idleWorkerIdxRef.current.push(idx);
      attachListeners(worker, idx);
    }
  }, [attachListeners]);

  const processFiles = useCallback(
    (files: File[], lang: string): void => {
      if (files.length === 0) return;
      ensureWorkerPool();
      const store = useAppStore.getState();
      for (const file of files) {
        const id = uuid();
        store.enqueue(id, file.name, file);
        queueRef.current.push({ id, file, lang });
      }
      pump();
    },
    [ensureWorkerPool, pump],
  );

  const reprocessWithManualQuad = useCallback(
    (id: string, manualQuad: ManualQuad, lang: string): void => {
      const session = useAppStore.getState().sessions.find((s) => s.id === id);
      if (session === undefined || session.originalFile === null) return;
      // Drop any earlier queued entry for this id — the user may be
      // re-cropping a session that's already pending; we don't want
      // both the old and new requests to run.
      queueRef.current = queueRef.current.filter((q) => q.id !== id);
      ensureWorkerPool();
      useAppStore.getState().resetForReprocess(id);
      queueRef.current.push({ id, file: session.originalFile, lang, manualQuad });
      pump();
    },
    [ensureWorkerPool, pump],
  );

  const reprocessWithRotation = useCallback(
    async (id: string, lang: string): Promise<void> => {
      const session = useAppStore.getState().sessions.find((s) => s.id === id);
      if (session === undefined) return;
      // Prefer the corrected JPEG — it's always present post-success and
      // always a plain JPEG, so canvas rotation works regardless of the
      // upload's source format (HEIC / PDF / etc.). Fall back to the
      // raw original if for some reason the corrected image is missing
      // (older session restored from IDB before correctedImage existed,
      // theoretically).
      const sourceBlob: Blob | null =
        session.correctedImage?.blob ?? session.originalFile;
      if (sourceBlob === null) return;
      const rotated = await rotateBlob90Clockwise(sourceBlob);
      const rotatedFile = new File([rotated], session.filename, {
        type: rotated.type,
      });
      queueRef.current = queueRef.current.filter((q) => q.id !== id);
      ensureWorkerPool();
      const store = useAppStore.getState();
      // Update originalFile FIRST so that if the user clicks rotate
      // again before this dispatch lands, the next rotation reads the
      // already-rotated image. resetForReprocess wipes it from IDB if
      // persistence is on; setResult will write the post-success
      // snapshot back.
      store.setOriginalFile(id, rotatedFile);
      store.resetForReprocess(id);
      queueRef.current.push({ id, file: rotatedFile, lang });
      pump();
    },
    [ensureWorkerPool, pump],
  );

  const cancel = useCallback((): void => {
    queueRef.current = [];
    inFlightByWorkerRef.current.clear();
    workerByIdRef.current.clear();
    idleWorkerIdxRef.current = [];
    inFlightFilesRef.current.clear();
    for (const worker of workersRef.current) worker.terminate();
    workersRef.current = [];
    useAppStore.getState().reset();
  }, []);

  const setDebugFolder = useCallback(
    (handle: FileSystemDirectoryHandle | null): void => {
      if (!DEBUG) return;
      debugFolderRef.current = handle;
    },
    [],
  );

  // The refs in the cleanup below intentionally read CURRENT state at
  // unmount (terminate every worker that's still alive, clear whatever
  // is still in flight). The "ref may have changed" warning is a
  // false positive for non-DOM refs holding workers + maps.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    return () => {
      queueRef.current = [];
      inFlightByWorkerRef.current.clear();
      workerByIdRef.current.clear();
      idleWorkerIdxRef.current = [];
      for (const worker of workersRef.current) worker.terminate();
      workersRef.current = [];
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  return {
    processFiles,
    cancel,
    setDebugFolder,
    reprocessWithManualQuad,
    reprocessWithRotation,
  };
}
